/**
 * CampaignRankingSheet
 *
 * Helper to open the campaign ranking sheet via SheetService. Mirrors the
 * imperative `show(...)` pattern of `InfoSheet` / `SubscribeUserSheet`.
 *
 * Replaces Flutter's bespoke drag-to-dismiss `AnimatedPositioned` surface
 * (lib/presentation/bottom_sheet/campaign_ranking_bottom_sheet.dart) with the
 * standard web `SheetService` / `ExpauseSheet` surface — a permitted UI
 * deviation; behaviour (title = campaign title, close button / click-outside to
 * dismiss) is equivalent. The sheet body lives in `CampaignRankingSheetContent`.
 *
 * Usage:
 *   CampaignRankingSheet.show({ campaign, screenKey });
 */
import { SheetService } from '@/presentation/components/common/sheet/service/SheetService.index';
import type { CampaignEntity } from '@/domain/entities/campaign/campaignEntity';
import { CampaignRankingSheetContent } from './CampaignRankingSheetContent';

export interface CampaignRankingSheetConfig {
  campaign: CampaignEntity;
  /** Screen key of the route opening this sheet. */
  screenKey?: string;
  /** Optional explicit container (defaults to screenKey-based resolution). */
  container?: HTMLElement | string | null;
  /**
   * Fixed sheet height. The caller must pass this (typically
   * `responsive.bottomSheet.maxHeight`) because this helper is a plain
   * function and cannot read `useResponsiveDesign()` itself.
   */
  fixedHeight?: string;
}

const show = (config: CampaignRankingSheetConfig): void => {
  SheetService.show({
    title: config.campaign.title,
    content: <CampaignRankingSheetContent campaign={config.campaign} />,
    container: config.container,
    screenKey: config.screenKey,
    fixedHeight: config.fixedHeight,
    qaId: 'campaign-ranking',
    closeOnClickOutside: true,
  });
};

export const CampaignRankingSheet = {
  show,
};
